import React, { useState, useEffect } from 'react';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import { X, Plus, Trash2, Package } from 'lucide-react';

export default function PackingPartsModal({ project, item, onClose, onSaved }) {
  const [parts, setParts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get(`/packing-parts/${item.id}`)
      .then(r => {
        if (r.data.length > 0) {
          setParts(r.data.map(p => ({ part_name: p.part_name, quantity: p.quantity, unit: p.unit || 'pcs', sort_order: p.sort_order })));
        } else {
          setParts([{ part_name: '', quantity: item.quantity || 1, unit: 'pcs', sort_order: 0 }]);
        }
      })
      .catch(() => setParts([{ part_name: '', quantity: item.quantity || 1, unit: 'pcs', sort_order: 0 }]))
      .finally(() => setLoading(false));
  }, [item.id]);

  const addPart = () => setParts([...parts, { part_name: '', quantity: item.quantity || 1, unit: 'pcs', sort_order: parts.length }]);
  const removePart = (idx) => setParts(parts.filter((_, i) => i !== idx));
  const updatePart = (idx, field, value) => {
    const updated = [...parts];
    updated[idx] = { ...updated[idx], [field]: value };
    setParts(updated);
  };

  const handleSave = async () => {
    if (parts.some(p => !p.part_name.trim())) return toast.error('Please enter names for all parts');
    setSaving(true);
    try {
      await api.post('/packing-parts', {
        project_item_id: item.id,
        project_id: project.id,
        parts: parts.map((p, i) => ({ ...p, sort_order: i }))
      });
      toast.success('Packing parts saved! ✅');
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Save failed');
    } finally { setSaving(false); }
  };

  const UNITS = ['pcs', 'box', 'set', 'pair', 'roll', 'kg', 'meter'];

  return (
    <div className="modal-backdrop">
      <div className="modal-scroll">
        <div className="modal-box" style={{maxWidth:560,display:"flex",flexDirection:"column",maxHeight:"90vh"}}>
        {/* Header */}
        <div className="modal-header">
          <div>
            <h2 className="text-lg font-bold flex items-center gap-2">
              <Package className="w-5 h-5 text-purple-600" /> Packing Parts Setup
            </h2>
            <p className="text-sm text-gray-500 mt-0.5">📦 {item.item_name} × {item.quantity}</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 p-1 rounded-lg hover:bg-gray-100">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Parts List */}
        <div className="flex-1 overflow-y-auto p-5">
          {loading ? (
            <div className="flex justify-center py-8">
              <div className="animate-spin w-6 h-6 border-4 border-purple-500 border-t-transparent rounded-full" />
            </div>
          ) : (
            <div className="space-y-3">
              <p className="text-xs text-gray-500 bg-gray-50 rounded-xl p-3">
                💡 Define each part that needs to be packed separately for this item
              </p>
              {parts.map((part, idx) => (
                <div key={idx} className="flex items-center gap-2 bg-gray-50 rounded-xl p-3">
                  <span className="text-xs font-bold text-gray-400 w-5 flex-shrink-0">{idx + 1}</span>
                  <input
                    value={part.part_name}
                    onChange={e => updatePart(idx, 'part_name', e.target.value)}
                    placeholder="Part name (e.g. Seat Cover, Legs Set)"
                    className="flex-1 border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-400 bg-white"
                  />
                  <input
                    type="number"
                    min="1"
                    value={part.quantity}
                    onChange={e => updatePart(idx, 'quantity', parseInt(e.target.value) || 1)}
                    className="w-20 border border-gray-200 rounded-xl px-2 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-400 bg-white text-center font-bold"
                  />
                  <select
                    value={part.unit}
                    onChange={e => updatePart(idx, 'unit', e.target.value)}
                    className="w-20 border border-gray-200 rounded-xl px-2 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-400 bg-white"
                  >
                    {UNITS.map(u => <option key={u} value={u}>{u}</option>)}
                  </select>
                  <button onClick={() => removePart(idx)} disabled={parts.length === 1}
                    className="text-red-400 hover:text-red-600 p-1 disabled:opacity-30">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
              <button onClick={addPart}
                className="w-full border-2 border-dashed border-purple-200 text-purple-600 hover:bg-purple-50 py-2.5 rounded-xl text-sm font-medium flex items-center justify-center gap-2 transition-colors">
                <Plus className="w-4 h-4" /> Add Part
              </button>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-5 border-t flex gap-3">
          <button onClick={onClose} className="flex-1 border border-gray-200 text-gray-600 py-2.5 rounded-xl text-sm hover:bg-gray-50">
            Cancel
          </button>
          <button onClick={handleSave} disabled={saving || loading}
            className="flex-1 bg-purple-600 hover:bg-purple-700 text-white py-2.5 rounded-xl text-sm font-bold disabled:opacity-50">
            {saving ? 'Saving...' : `💾 Save Parts (${parts.length})`}
          </button>
        </div>
      </div>
    </div>
        </div>
  );
}
